import React from "react";
import AppShell from "@/components/AppShell";
import LandingPersonaleTab from "@/components/LandingPersonaleTab";
import DismissibleIntro from "@/components/DismissibleIntro";
import ReopenGuidesBtn from "@/components/ReopenGuidesBtn";
import { Sparkle, WhatsappLogo, QrCode, ChatCircleText, Globe, Printer } from "@phosphor-icons/react";

/**
 * /dashboard/landing — "Trovo proprietari" (iter v28 r367 · User M1277).
 *
 * UN unico strumento di lead-gen, due formati visibili sotto:
 *   1. Pagina online (`/stima/{code}`) — interattiva, condivisibile via link
 *   2. Volantino A5 + story Instagram — versione cartacea / stampabile
 * Stesso brand, stessi colori, stesso link finale. Niente più confusione
 * tra "pagina personale" e "volantino".
 *
 * NOTA: rinominata da "Il tuo link stima" → "Trovo proprietari" come da
 * scelta utente. URL della route resta `/dashboard/landing` per non
 * rompere bookmark/links interni.
 */
export default function MiaLanding() {
  return (
    <AppShell title="Trovo proprietari" breadcrumb="Trovo proprietari">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 py-6">

        {/* iter v28 r368 — User M1278: ogni descrizione è dismissibile.
            Riapri tutte le guide della sezione col bottone "?". */}
        <div className="flex items-center justify-end mb-2">
          <ReopenGuidesBtn
            keys={["trovo-prop:intro", "trovo-prop:come-funziona", "trovo-prop:formati"]}
            testId="trovo-prop-reopen-guides"
          />
        </div>

        {/* Hero — un'unica frase di scope, niente wall of text */}
        <DismissibleIntro storageKey="trovo-prop:intro">
          <div className="mb-6">
            <div className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.18em] font-semibold text-[#5A6A4D]">
              <Sparkle size={14} weight="fill" /> Il tuo strumento da agente
            </div>
            <h1 className="font-editorial text-[28px] sm:text-[36px] leading-tight mt-1 text-[#1C1C1A]" data-testid="mia-landing-hero">
              Trovo proprietari<br/>
              <span className="italic text-[#5A6A4D]">che vogliono vendere.</span>
            </h1>
            <p className="mt-3 text-[14.5px] text-[#3F3D38] max-w-2xl leading-relaxed">
              Hai uno strumento tuo personale: i proprietari aprono la <strong>tua pagina</strong> o ricevono il <strong>tuo volantino</strong>, vedono in 30 secondi quanto vale casa loro, e se interessati ti contattano.
            </p>
          </div>
        </DismissibleIntro>

        {/* Come funziona — 3 step compatti */}
        <DismissibleIntro storageKey="trovo-prop:come-funziona">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-8">
            <div className="bg-white border border-[#E5E7E0] rounded-2xl p-4">
              <div className="flex items-center gap-2 text-[10.5px] uppercase tracking-[0.16em] font-bold text-[#7C8B6E]">
                <span className="w-5 h-5 rounded-full bg-[#1C1C1A] text-white inline-flex items-center justify-center text-[10px]">1</span>
                Condividi
              </div>
              <div className="mt-2 text-[13.5px] text-[#1C1C1A] leading-snug">
                Mandi il link su <WhatsappLogo size={13} className="inline -mt-0.5" weight="fill"/> <strong>WhatsApp</strong> · Instagram · o stampi il <QrCode size={13} className="inline -mt-0.5" weight="bold"/> <strong>QR</strong> del volantino.
              </div>
            </div>
            <div className="bg-white border border-[#E5E7E0] rounded-2xl p-4">
              <div className="flex items-center gap-2 text-[10.5px] uppercase tracking-[0.16em] font-bold text-[#7C8B6E]">
                <span className="w-5 h-5 rounded-full bg-[#1C1C1A] text-white inline-flex items-center justify-center text-[10px]">2</span>
                Il proprietario apre
              </div>
              <div className="mt-2 text-[13.5px] text-[#1C1C1A] leading-snug">
                4 dati sulla casa → vede <strong>quanto vale</strong>. Niente account, niente dati personali per la stima.
              </div>
            </div>
            <div className="bg-[#1C1C1A] text-white border border-[#1C1C1A] rounded-2xl p-4">
              <div className="flex items-center gap-2 text-[10.5px] uppercase tracking-[0.16em] font-bold text-[#A8B89A]">
                <span className="w-5 h-5 rounded-full bg-[#7C8B6E] text-white inline-flex items-center justify-center text-[10px]">3</span>
                Se vuole, ti contatta
              </div>
              <div className="mt-2 text-[13.5px] leading-snug">
                Lo trovi in <strong>Richieste ricevute</strong> · <ChatCircleText size={13} weight="fill" className="inline -mt-0.5 text-[#A8B89A]"/> lo richiami per la perizia.
              </div>
            </div>
          </div>
        </DismissibleIntro>

        {/* iter v28 r367 — Due tab visibili: Online + Volantino.
            iter v28 r371 — User M1281: rimossi i 2 tile sopra il box nero
            (erano ridondanti col box stesso). Il pannello unico nero +
            CTA volantino sotto bastano da soli. */}

        {/* Sezione online (link + qr + preview) — già implementata in LandingPersonaleTab */}
        <div id="sezione-online" className="scroll-mt-20">
          <LandingPersonaleTab />
        </div>
      </div>
    </AppShell>
  );
}
